import {on} from 'delegated-events'

type ArtifactFileEntry = {
  path: string
  size: number
  compressed_size: number
  compression_method: number
  byte_range_start: number
  byte_range_end: number
}

// Keep byte formatting in-client to avoid extra server formatting branches.
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

async function inflateRawToBlob(compressedBlob: Blob): Promise<Blob> {
  // Use platform decompression stream so we can support file extraction without
  // extra dependencies for DEFLATE raw decoding.
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('Your browser does not support artifact file decompression')
  }

  const ds = new DecompressionStream('deflate-raw')
  const decompressedStream = compressedBlob.stream().pipeThrough(ds)
  const response = new Response(decompressedStream)
  return await response.blob()
}

type PaginationState = {
  allFiles: ArtifactFileEntry[]
  listRoot: HTMLElement
  paginationRoot: HTMLElement
  paginationTemplatesByPage: Map<string, HTMLTemplateElement>
  perPage: number
  totalPages: number
  currentPage: number
  pageBeforeSearch: number
  isSearching: boolean
}

const paginationStateByRoot = new WeakMap<HTMLElement, PaginationState>()

function renderRows(
  root: HTMLElement,
  listRoot: HTMLElement,
  allFiles: ArtifactFileEntry[],
  page: number,
  perPage: number,
): void {
  const start = (page - 1) * perPage
  const end = start + perPage
  const rows = allFiles.slice(start, end)
  const fileIconTemplate = root.querySelector<HTMLTemplateElement>('.js-file-icon-template')
  const downloadIconTemplate = root.querySelector<HTMLTemplateElement>('.js-download-icon-template')
  const compressedLabel = root.getAttribute('data-msg-compressed-label') || ''
  const compressedTitle = root.getAttribute('data-msg-compressed-title') || ''
  const downloadAriaPrefix = root.getAttribute('data-msg-download-aria-prefix') || ''

  listRoot.textContent = ''

  for (const file of rows) {
    const filePath = String(file.path || '')

    const row = document.createElement('li')
    row.className = 'd-flex flex-items-center py-2 border-bottom'

    const fileIconSpan = document.createElement('span')
    fileIconSpan.className = 'mr-2 color-fg-muted flex-shrink-0'
    if (fileIconTemplate) {
      fileIconSpan.append(fileIconTemplate.content.cloneNode(true))
    }
    row.append(fileIconSpan)

    const filePathSpan = document.createElement('span')
    filePathSpan.className = 'flex-auto min-width-0 text-small'
    filePathSpan.style.wordBreak = 'break-all'
    filePathSpan.textContent = filePath
    row.append(filePathSpan)

    const fileSizeSpan = document.createElement('span')
    fileSizeSpan.className = 'ml-2 text-small color-fg-muted no-wrap flex-shrink-0'
    fileSizeSpan.textContent = formatBytes(file.size)

    if (Number(file.compression_method) === 8) {
      const compressedBadge = document.createElement('span')
      compressedBadge.className = 'ml-1'
      compressedBadge.title = compressedTitle
      compressedBadge.textContent = compressedLabel
      fileSizeSpan.append(compressedBadge)
    }

    row.append(fileSizeSpan)

    const downloadLink = document.createElement('button')
    downloadLink.type = 'button'
    downloadLink.className = 'btn-link ml-2 flex-shrink-0 artifact-file-download-link'
    downloadLink.setAttribute('data-file-name', filePath.split('/').pop() || '')
    downloadLink.setAttribute('data-byte-range-start', String(file.byte_range_start))
    downloadLink.setAttribute('data-byte-range-end', String(file.byte_range_end))
    downloadLink.setAttribute('data-compressed-size', String(file.compressed_size))
    downloadLink.setAttribute('data-uncompressed-size', String(file.size))
    downloadLink.setAttribute('data-needs-decompression', Number(file.compression_method) === 8 ? 'true' : 'false')
    downloadLink.setAttribute('aria-label', `${downloadAriaPrefix}${filePath}`)
    if (downloadIconTemplate) {
      downloadLink.append(downloadIconTemplate.content.cloneNode(true))
    }

    row.append(downloadLink)
    listRoot.append(row)
  }
}

function renderPaginationFromCache(
  paginationRoot: HTMLElement,
  paginationTemplatesByPage: Map<string, HTMLTemplateElement>,
  page: number,
): void {
  const template = paginationTemplatesByPage.get(String(page))
  if (!template) return

  paginationRoot.textContent = ''
  paginationRoot.append(template.content.cloneNode(true))
}

function initArtifactFilesPagination(root: HTMLElement): void {
  if (root.getAttribute('data-client-pagination-initialized') === 'true') return

  const paginationRoot = root.querySelector<HTMLElement>('.js-artifact-pagination')
  const listRoot = root.querySelector<HTMLElement>('.js-artifact-files-list')
  const cacheNode = root.querySelector<HTMLElement>('.js-artifact-files-cache')
  if (!paginationRoot || !listRoot || !cacheNode) return

  let allFiles: ArtifactFileEntry[] = []
  try {
    allFiles = JSON.parse(cacheNode.textContent || '[]') as ArtifactFileEntry[]
  } catch {
    return
  }

  const paginationTemplatesByPage = new Map<string, HTMLTemplateElement>()
  for (const tmpl of root.querySelectorAll<HTMLTemplateElement>('.js-artifact-pagination-template')) {
    const pageKey = tmpl.getAttribute('data-page')
    if (pageKey) {
      paginationTemplatesByPage.set(pageKey, tmpl)
    }
  }

  const currentPage = Number.parseInt(root.getAttribute('data-current-page') || '1', 10)
  const perPage = Number.parseInt(root.getAttribute('data-per-page') || '25', 10)
  const totalPages = Number.parseInt(root.getAttribute('data-total-pages') || '1', 10)

  paginationStateByRoot.set(root, {
    allFiles,
    listRoot,
    paginationRoot,
    paginationTemplatesByPage,
    perPage,
    totalPages,
    currentPage,
    pageBeforeSearch: currentPage,
    isSearching: false,
  })

  root.setAttribute('data-client-pagination-initialized', 'true')
}

function initAllArtifactFilesPagination(): void {
  for (const root of document.querySelectorAll<HTMLElement>('.js-artifact-files')) {
    initArtifactFilesPagination(root)
    initArtifactFilesSearch(root)
  }
}

function getPageFromPaginationLink(link: HTMLAnchorElement): number {
  const dataPage = Number.parseInt(link.getAttribute('data-page') || '', 10)
  if (Number.isFinite(dataPage)) return dataPage

  try {
    const url = new URL(link.href, window.location.origin)
    const page = Number.parseInt(url.searchParams.get('page') || '', 10)
    if (Number.isFinite(page)) return page
  } catch {
    return Number.NaN
  }

  return Number.NaN
}

function applyFileSearch(root: HTMLElement, state: PaginationState, rawQuery: string): void {
  const query = rawQuery.trim().toLowerCase()

  if (query.length > 0) {
    if (!state.isSearching) {
      state.pageBeforeSearch = state.currentPage
    }
    state.isSearching = true

    const matchedFiles = state.allFiles.filter(file => file.path.toLowerCase().includes(query))
    renderRows(root, state.listRoot, matchedFiles, 1, matchedFiles.length > 0 ? matchedFiles.length : 1)
    state.paginationRoot.hidden = true
    state.paginationRoot.style.display = 'none'
    return
  }

  state.isSearching = false
  state.currentPage = state.pageBeforeSearch
  renderRows(root, state.listRoot, state.allFiles, state.currentPage, state.perPage)
  renderPaginationFromCache(state.paginationRoot, state.paginationTemplatesByPage, state.currentPage)
  state.paginationRoot.hidden = false
  state.paginationRoot.style.display = ''
}

// The any is unavoidable here since the generic debounce signature
// needs to accept arbitrary argument lists, and unknown[]
// isn't assignable in this position.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => void>(fn: T, wait: number): T {
  let timeoutId: number | undefined
  return function (...args: Parameters<T>) {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId)
    }
    timeoutId = window.setTimeout(() => {
      fn(...args)
    }, wait)
  } as T
}

function initArtifactFilesSearch(root: HTMLElement): void {
  const input = root.querySelector<HTMLInputElement>('.js-artifact-files-search')
  if (!input) return
  if (input.getAttribute('data-search-initialized') === 'true') return

  const debouncedSearch = debounce((value: string) => {
    if (!paginationStateByRoot.has(root)) {
      initArtifactFilesPagination(root)
    }

    const state = paginationStateByRoot.get(root)
    if (!state) return

    applyFileSearch(root, state, value)
  }, 150)

  input.addEventListener('input', () => {
    debouncedSearch(input.value)
  })

  if (!paginationStateByRoot.has(root)) {
    initArtifactFilesPagination(root)
  }

  const state = paginationStateByRoot.get(root)
  if (state) {
    applyFileSearch(root, state, input.value)
  }

  input.setAttribute('data-search-initialized', 'true')
}

initAllArtifactFilesPagination()
document.addEventListener('DOMContentLoaded', initAllArtifactFilesPagination)

function triggerFileDownload(blob: Blob, fileName: string): void {
  // Browser download behavior varies across environments; this helper centralizes
  // fallbacks so callers keep one path.

  const nav = window.navigator as Navigator & {
    msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean
  }

  if (typeof nav.msSaveOrOpenBlob === 'function') {
    // Legacy fallback for environments that don't honor anchor download.
    nav.msSaveOrOpenBlob(blob, fileName)
    return
  }

  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = fileName
  link.rel = 'noopener'
  link.style.display = 'none'
  document.body.append(link)

  link.click()

  window.setTimeout(() => {
    link.remove()
  }, 1_000)

  window.setTimeout(() => {
    // Delay revoke so slower browsers have time to start consuming blob URL.
    URL.revokeObjectURL(blobUrl)
  }, 60_000)
}

type SignedUrlCacheEntry = {
  signedUrl: string
  expiresAtMs: number
}

const signedUrlCacheByEndpoint = new Map<string, SignedUrlCacheEntry>()
const minimumSignedUrlValidityMs = 30_000

function inferSignedUrlExpiryMs(signedUrl: string): number | undefined {
  let parsedUrl: URL
  try {
    parsedUrl = new URL(signedUrl, window.location.origin)
  } catch {
    return undefined
  }

  const azureExpiry = parsedUrl.searchParams.get('se')
  if (!azureExpiry) return undefined

  const azureExpiryMs = Date.parse(azureExpiry)
  return Number.isFinite(azureExpiryMs) ? azureExpiryMs : undefined
}

async function getSignedUrl(root: HTMLElement): Promise<string> {
  // Signed URLs are short-lived; fetch on demand per click.
  const endpoint = root.getAttribute('data-signed-url-endpoint')
  if (!endpoint) {
    throw new Error('Missing signed URL endpoint')
  }

  const cachedEntry = signedUrlCacheByEndpoint.get(endpoint)
  if (cachedEntry && cachedEntry.expiresAtMs - Date.now() > minimumSignedUrlValidityMs) {
    return cachedEntry.signedUrl
  }

  const response = await fetch(endpoint, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to get signed URL: ${response.status}`)
  }

  const data = (await response.json()) as {signed_url?: string}
  if (!data.signed_url) {
    throw new Error('Signed URL response was empty')
  }

  const expiresAtMs = inferSignedUrlExpiryMs(data.signed_url)
  if (expiresAtMs) {
    signedUrlCacheByEndpoint.set(endpoint, {
      signedUrl: data.signed_url,
      expiresAtMs,
    })
  } else {
    signedUrlCacheByEndpoint.delete(endpoint)
  }

  return data.signed_url
}

function resolveFileEntryFromRow(root: HTMLElement, button: HTMLElement): ArtifactFileEntry | undefined {
  const listItem = button.closest<HTMLElement>('li')
  const pathNode = listItem?.querySelector<HTMLElement>('span.flex-auto')
  const filePath = pathNode?.textContent?.trim()
  if (!filePath) return undefined

  const paginationState = paginationStateByRoot.get(root)
  if (!paginationState) return undefined

  return paginationState.allFiles.find(file => file.path === filePath)
}

async function downloadFile(button: HTMLElement): Promise<void> {
  // Click handler intentionally performs full flow (signed URL, range read,
  // optional inflate, save) to keep UX in one action.
  const root = button.closest<HTMLElement>('.js-artifact-files')
  if (!root) return

  const fileEntryFromRow = resolveFileEntryFromRow(root, button)

  const fileName = button.getAttribute('data-file-name') || fileEntryFromRow?.path.split('/').pop() || null
  const byteRangeStart =
    button.getAttribute('data-byte-range-start') ||
    (fileEntryFromRow ? String(fileEntryFromRow.byte_range_start) : null)
  const byteRangeEnd =
    button.getAttribute('data-byte-range-end') || (fileEntryFromRow ? String(fileEntryFromRow.byte_range_end) : null)
  const needsDecompressionAttr = button.getAttribute('data-needs-decompression')
  const needsDecompression = needsDecompressionAttr
    ? needsDecompressionAttr === 'true'
    : Number(fileEntryFromRow?.compression_method) === 8

  if (!fileName || !byteRangeStart || !byteRangeEnd) {
    return
  }

  try {
    const signedUrl = await getSignedUrl(root)
    const dataResponse = await fetch(signedUrl, {
      headers: {Range: `bytes=${byteRangeStart}-${byteRangeEnd}`},
    })

    if (!dataResponse.ok && dataResponse.status !== 206) {
      throw new Error(`Failed to download file data: ${dataResponse.status}`)
    }

    const compressedBlob = await dataResponse.blob()

    let fileBlob: Blob
    if (needsDecompression) {
      fileBlob = await inflateRawToBlob(compressedBlob)
    } else {
      fileBlob = compressedBlob
    }
    triggerFileDownload(fileBlob, fileName)
  } catch (error) {
    throw error
  }
}

on('click', '.download-file-btn, .artifact-file-download-link', event => {
  event.preventDefault()
  const button = event.currentTarget as HTMLElement
  if (!button) return

  downloadFile(button)
})

on('click', '.js-artifact-pagination a', event => {
  event.preventDefault()

  const link = event.currentTarget as HTMLAnchorElement
  const root = link.closest<HTMLElement>('.js-artifact-files')
  if (!root) return

  if (!paginationStateByRoot.has(root)) {
    initArtifactFilesPagination(root)
  }

  const state = paginationStateByRoot.get(root)
  if (!state) return
  if (state.isSearching) return

  const page = getPageFromPaginationLink(link)
  if (!Number.isFinite(page)) return

  if (page < 1 || page > state.totalPages) return

  state.currentPage = page
  renderRows(root, state.listRoot, state.allFiles, state.currentPage, state.perPage)
  renderPaginationFromCache(state.paginationRoot, state.paginationTemplatesByPage, state.currentPage)
})
